// deps
import { useCallback, useContext, useMemo, useRef } from "react";

// internal-deps
import {
    WalletData,
    WalletPaymentGroupActions,
    WalletPaymentMethodData,
    WalletSpecificActions,
} from "@swiggy-private/payment-ui";

// interfaces
import { VALID_WALLET_LINK_STATUSES, WalletApiFetchState } from "@swiggy-private/payment-core";
import { PaymentGroupHandlerProps } from "@payment-client/components/show-payments/components/payment-group-handler/interfaces";

// hooks
import {
    useNavigate,
    User,
    UserContext,
    CheckoutRoute,
    AnalyticsContext,
    getUserFromUserContextValue,
    recordNREvent,
    NEWRELIC_TABLES,
    WP_WALLET_BALANCE_STATE,
} from "@swiggy-private/checkout-core";
import { usePaymentActions } from "./UsePaymentActions";
import { useAddMoney } from "@payment-client/hooks/UseAddMoney";
import { defaultWalletData, useWalletBalanceData } from "@payment-client/hooks/UseWalletBalanceData";
import { useAmazonPayHandler } from "./UseAmazonPayHandler";

// contexts
import { PaymentPayContext } from "@payment-client/contexts/PaymentPayContext";
import { PhonepeContext } from "@payment-client/contexts/PhonepeSdkContext";

// analytics
import { onAddMoneyImpressionEvent } from "@payment-client/analytics/events/Payment";

// events
import { clickLinkWalletEvent } from "@payment-client/analytics/events/LinkWallet";

interface UseWalletHandlerReturnType {
    paymentActions: WalletPaymentGroupActions;
    walletSpecificActions: WalletSpecificActions;
    showProgressLoader: boolean;
    user?: User;
}

interface UseWalletHandlerProps {
    onPay: PaymentGroupHandlerProps["onPay"];
    onSelect: PaymentGroupHandlerProps["onSelect"];
    onLoad?: PaymentGroupHandlerProps["onLoad"];
    paymentAmount: string;
}

export const useWalletHandler = (props: UseWalletHandlerProps): UseWalletHandlerReturnType => {
    const actions = usePaymentActions({
        onPay: props.onPay,
        onSelect: props.onSelect,
        onLoad: props.onLoad,
    });
    const { user: userContextValue } = useContext(UserContext);
    const { isPhonepeSdkAvailable } = useContext(PhonepeContext);
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const { billTotal } = useContext(PaymentPayContext);
    const [addMoneyInProgress, addMoneyToWallet] = useAddMoney();
    const { linkAmazonPaySdk, getAmazonPaySdkStatus, showLoader, getAmazonPayMetaId } = useAmazonPayHandler(
        Number(props.paymentAmount),
    );

    // Ref to track API call attempts per wallet method (max 3 attempts)
    const apiCallAttemptsRef = useRef<Record<string, number>>({});

    const navigateTo = useNavigate();

    const paymentAmount = Number(props.paymentAmount);
    const walletBalanceState = useWalletBalanceData({
        paymentAmount,
    });

    const user = useMemo(() => getUserFromUserContextValue(userContextValue), [userContextValue]);

    // Tracks the last PhonePe availability value logged to NR so we emit once on first check and
    // again only if availability flips (bounded volume, unlike logging on every render).
    const phonepeLoggedValueRef = useRef<boolean | null>(null);
    const getPhonepeSdkStatus = useCallback(() => {
        // (2.9) PhonePe SDK availability was checked but never logged to NR.
        if (phonepeLoggedValueRef.current !== isPhonepeSdkAvailable) {
            phonepeLoggedValueRef.current = isPhonepeSdkAvailable;
            recordNREvent({
                eventType: NEWRELIC_TABLES.WP_WALLET_BALANCE,
                state: WP_WALLET_BALANCE_STATE.PHONEPEPAY_SDK_STATUS_CHECK,
                isPhonepeSdkAvailable: String(isPhonepeSdkAvailable),
            });
        }
        return {
            isPhonepeSdkAvailable,
        };
    }, [isPhonepeSdkAvailable]);

    const getWalletBalance = useCallback(
        (methodData: WalletPaymentMethodData): [WalletApiFetchState, WalletData] => {
            const state = walletBalanceState[methodData.name];

            // This is to handle invalid wallet issue temporarily.
            // https://sentry.io/organizations/bundl-technologies-pvt-ltd/issues/3261654210/?project=5168575&query=is%3Aunresolved
            if (state == null) {
                // sending NR event to identify invalid wallet instances and find the root cause
                recordNREvent({
                    eventType: NEWRELIC_TABLES.INVALID_WALLET,
                    name: methodData.name,
                    currentWallets: Object.keys(walletBalanceState).toString(),
                });
                return [
                    WalletApiFetchState.ERROR,
                    {
                        ...defaultWalletData,
                        linkStatus: VALID_WALLET_LINK_STATUSES.RETRY,
                    },
                ];
            }

            // Initialize attempt count for this method if not exists
            if (!apiCallAttemptsRef.current[methodData.name]) {
                apiCallAttemptsRef.current[methodData.name] = 0;
            }

            // Reset attempt count if state is SUCCESS (successful API response)
            if (state.fetchState === WalletApiFetchState.SUCCESS) {
                apiCallAttemptsRef.current[methodData.name] = 0;
            }

            // skip calling api if the last reducer state was SUCCESS or LOADING
            // or if we've already attempted 3 times
            if (
                state.fetchState === WalletApiFetchState.SUCCESS ||
                state.fetchState === WalletApiFetchState.LOADING ||
                apiCallAttemptsRef.current[methodData.name] >= 3
            ) {
                return [state.fetchState, state.data];
            }

            // Only make API call if we haven't exceeded 3 attempts
            if (
                (state.fetchState === WalletApiFetchState.DEFAULT || state.fetchState === WalletApiFetchState.ERROR) &&
                apiCallAttemptsRef.current[methodData.name] < 3
            ) {
                apiCallAttemptsRef.current[methodData.name] += 1;
                const attemptCount = apiCallAttemptsRef.current[methodData.name];

                // (2.4) Auto-retry logic ran with no observability so far.
                recordNREvent({
                    eventType: NEWRELIC_TABLES.WP_WALLET_BALANCE,
                    state: WP_WALLET_BALANCE_STATE.RETRY_TRIGGERED,
                    walletCode: methodData.name,
                    attemptCount,
                    fetchState: String(state.fetchState),
                });

                // (2.5) Fires exactly once, when the final (3rd) attempt is triggered — the wallet is
                // now capped and will stop retrying while still not SUCCESS.
                if (attemptCount === 3) {
                    recordNREvent({
                        eventType: NEWRELIC_TABLES.WP_WALLET_BALANCE,
                        state: WP_WALLET_BALANCE_STATE.MAX_RETRIES_HIT,
                        walletCode: methodData.name,
                        finalFetchState: String(state.fetchState),
                    });
                }

                state.refetch();
            }

            return [state.fetchState, state.data];
        },
        [walletBalanceState],
    );

    const refetchBalance = useCallback(
        async (methodData: WalletPaymentMethodData): Promise<void> => {
            if (walletBalanceState[methodData.name]) {
                // Reset attempt count when explicitly refetching
                apiCallAttemptsRef.current[methodData.name] = 0;
                return walletBalanceState[methodData.name].refetch();
            }
        },
        [walletBalanceState],
    );

    const linkWallet = useCallback(
        (walletCode: string, isOldWalletLinkFlow: boolean): void => {
            navigateTo(CheckoutRoute.LINK_WALLET_WITH_NUMBER, { walletCode, isOldWalletLinkFlow });
        },
        [navigateTo],
    );

    const onLinkWalletClick = useCallback(
        (walletCode: string): void => {
            clickLinkWalletEvent(walletCode, clickEvent);
        },
        [clickEvent],
    );

    const onAddMoneyImpression = useCallback(
        (walletCode: string): void => {
            onAddMoneyImpressionEvent(billTotal, walletCode, impressionEvent);
        },
        [impressionEvent, billTotal],
    );

    const walletSpecificActions = useMemo(
        (): WalletSpecificActions => ({
            getWalletBalance,
            refetchBalance,
            linkWallet,
            onLinkWalletClick,
            onAddMoney: addMoneyToWallet,
            onAddMoneyImpression,
            getAmazonPaySdkStatus,
            linkAmazonPaySdk,
            getPhonepeSdkStatus,
            getAmazonPayMetaId,
        }),
        [
            addMoneyToWallet,
            getAmazonPaySdkStatus,
            getPhonepeSdkStatus,
            getWalletBalance,
            linkAmazonPaySdk,
            linkWallet,
            onAddMoneyImpression,
            onLinkWalletClick,
            refetchBalance,
            getAmazonPayMetaId,
        ],
    );

    const paymentActions = useMemo(
        (): WalletPaymentGroupActions => ({
            ...actions,
            ...walletSpecificActions,
        }),
        [actions, walletSpecificActions],
    );

    return {
        paymentActions,
        walletSpecificActions,
        user,
        showProgressLoader: showLoader || addMoneyInProgress,
    };
};
